import { useRef } from 'react';
import { Flex, Text, Icon } from '@kognitos/lattice';
import { useHighlightReplay } from '../shared/useHighlightReplay';

/**
 * Props for the ConfigFormRow component
 */
export interface IConfigFormRowProps {
  /** Label text for the form field */
  label: string;
  /** Optional description text below the label */
  description?: string;
  /** The input element to render */
  children: React.ReactNode;
  /** Whether the row is disabled (applies opacity) */
  disabled?: boolean;
  /** Optional validation message displayed below the input. */
  error?: string;
  /**
   * When true, briefly pulses the row and shows a ✨ marker — used to flag that
   * the assistant just filled this field, and scrolls it into view.
   */
  highlighted?: boolean;
  /**
   * Monotonic nonce that changes on every fill. Depended on by the highlight
   * effect so re-filling the same field replays the pulse/scroll (the effect
   * restarts the CSS animation via a reflow) even when `highlighted` is unchanged.
   */
  highlightNonce?: number;
}

/**
 * A single configuration form row with label, description, and input.
 * Used in webhook trigger dialogs for consistent form layout.
 *
 * @param props - Component props
 * @returns JSX element containing the form row
 */
export function ConfigFormRow({
  label,
  description,
  children,
  disabled,
  error,
  highlighted = false,
  highlightNonce,
}: IConfigFormRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  useHighlightReplay(rowRef, highlighted, highlightNonce);

  return (
    <Flex
      ref={rowRef}
      direction="row"
      align="start"
      gap={4}
      className={`p-2.5 rounded-md w-full ${disabled ? 'opacity-50' : ''} ${
        highlighted ? 'trigger-fill-highlight' : ''
      }`}
    >
      {/* Label Section */}
      <Flex direction="column" gap={1} className="w-[200px] shrink-0">
        <Flex direction="row" align="center" gap={1}>
          <Text className="font-medium text-sm">{label}</Text>
          {highlighted && (
            <Icon
              type="Sparkle"
              size="sm"
              aria-hidden
              className="text-primary trigger-fill-sparkle shrink-0"
            />
          )}
        </Flex>
        {description && (
          <Text className="text-xs text-muted-foreground">{description}</Text>
        )}
      </Flex>

      {/* Input Section */}
      <Flex direction="column" gap={1} className="flex-1">
        {children}
        {error && (
          <Text className="text-xs text-destructive" role="alert">
            {error}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
